import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import "./FeedbackModal.css";
import { CHAT_API_ENDPOINTS } from "../../api/CHAT_API_ENDPOINTS";

const SimpleFeedbackModal = ({
  isOpen,
  onClose,
  onSubmit,
  feedbackType,
  chatRecordId,
  userId,
}) => {
    const { t, i18n  } = useTranslation();
  
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    feedback: false,
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [internalModalOpen, setInternalModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setInternalModalOpen(true);
      setTimeout(() => setModalVisible(true), 10);
    } else {
      setModalVisible(false);
      // Don't close internal modal immediately, let the success flow handle it
    }
  }, [isOpen]);

  // Don't return null if success toast is showing or internal modal is open
  if (!isOpen && !showSuccessToast && !internalModalOpen) return null;

  const validateForm = () => {
    const errors = {
      feedback: feedbackText.trim() === "",
    };

    setValidationErrors(errors);
    return !errors.feedback;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    const success = await submitFeedback(
      chatRecordId,
      feedbackType === "positive" ? 1 : -1,
      feedbackText
    );

    setIsSubmitting(false);

    if (success) {
      // First close the modal visually
      setModalVisible(false);
      
      // After modal close animation, show success toast
      setTimeout(() => {
        setInternalModalOpen(false); // Close internal modal
        setShowSuccessToast(true);
        
        // Hide success message after 2 seconds and then close everything
        setTimeout(() => {
          setShowSuccessToast(false);
          setFeedbackText("");
          setShowSuccess(false);
          onSubmit(feedbackText, success);
          onClose(); // Now it's safe to call onClose
        }, 2000);
      }, 300); // Wait for modal close animation to complete
    } else {
      onSubmit(feedbackText, false);
    }
  };

  const submitFeedback = async (chatRecordId, feedbackValue, textFeedback) => {
    try {
      const requestBody = {
        chat_record_id: chatRecordId,
        user_feedback: feedbackValue,
        feedback_text: textFeedback || "",
        rating: feedbackValue === 1 ? 5 : 2,
        user_id:
          sessionStorage.getItem("user_id") ||
          localStorage.getItem("user_id") ||
          userId ||
          "",
      };

      const response = await fetch(CHAT_API_ENDPOINTS.FEEDBACK_PARTICULAR, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error("Error submitting feedback:", error);
      return false;
    }
  };

  const handleClose = () => {
    setModalVisible(false);
    setTimeout(() => {
      setInternalModalOpen(false);
      onClose();
    }, 300);
  };

  return (
    <>
      {/* Success Toast - Outside modal container */}
      {showSuccessToast && (
        <div className="fixed top-0 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center feedsubmitPnc">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {t('feedbackSubmittedSuccessfully')}
        </div>
      )}
      
      {/* Modal Container - Only show if internal modal is open */}
      {internalModalOpen && (
        <div
          className={`fixed modal-feedback inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 transition-opacity mt-0 duration-300 ${
            modalVisible ? "opacity-100" : "opacity-0"
          }`}
          onClick={handleClose}
        >
          {/* Modal Container */}
          <div
            className={`w-full sm:max-w-lg mx-auto overflow-hidden feebBorderPnc shadow-lg transition-transform duration-300 sm:rounded-lg ${
              modalVisible
                ? "translate-y-0"
                : "translate-y-full sm:translate-y-0 sm:scale-95"
            }`}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: "90vh",
              width: "100%",
              borderRadius: window.innerWidth < 640 ? "1rem 1rem 0 0" : undefined,
            }}
          >
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 feedHeader">
              <h2 className="FeedHeaderPnc">{t('feedback')}</h2>
              <button
                onClick={handleClose}
                className="text-black crossFeedBtnPnc"
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 bg-[#ffffff] contentModalPnc">
              <div className="text-center">
                <h3 className="font-medium subHeadingFeedPnc">
                  {feedbackType === "positive"
                    ? t('positiveExperienceMessage')
                    : t('negativeExperienceMessage')}
                </h3>

                {/* Feedback Text Area */}
                <div className="mb-[24px]">
                  <textarea
                    placeholder={t('feedbackPlaceholder')}
                    className={`w-full border ${
                      validationErrors.feedback
                        ? "border-red-500"
                        : "border-gray-300"
                    } rounded-lg p-[0.7rem] focus:outline-none focus:ring-red-500 focus:border-red-500 min-h-32 resize-none`}
                    value={feedbackText}
                    onChange={(e) => {
                      setFeedbackText(e.target.value);
                      if (e.target.value.trim() !== "") {
                        setValidationErrors({
                          ...validationErrors,
                          feedback: false,
                        });
                      }
                    }}
                  ></textarea>
                  {validationErrors.feedback && (
                    <p className="text-red-500 text-sm text-left mt-1">
                      {t('feedbackRequired')}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-center gap-3">
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black submitFeedPnc disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center">{t('submit')}</div>
                    ) : (
                      t('submit')
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SimpleFeedbackModal;